import { SignInModalTab } from '../templates/_shared/ui-components/SignInModal/types';
import { getCookie } from './cookies';
import { IS_NATIVE_APP_CLIENT } from './helpers';

const FACE_ID_ENABLED_CUTOMER_DETAILS = 'faceIdEnabledCutomerDetails';

const getUserLoggedInCountLocalStorageKey = (email: string) => 'user_logged_in_count_' + btoa(email);

const getUserFaceIdCancelledCountLocalStorageKey = (customerId: string) =>
  `user_${btoa(customerId).replace(/=*$/, '')}_faceid_cancelled_count`;

const getUserCancelledFaceIdCookieKey = (customerId: string) =>
  `user_${btoa(customerId).replace(/=*$/, '')}_faceid_cancelled`;

const storeUserDetailsToLS = (data: unknown) => {
  window.localStorage.setItem(FACE_ID_ENABLED_CUTOMER_DETAILS, btoa(JSON.stringify(data)));
};

const isSafariOnIOS = (): boolean => {
  // this cookie is to faceid test purpose
  if (getCookie('enable-faceid-test') === 'true') {
    return true;
  }
  const userAgent: string = navigator.userAgent;
  const isIOS: boolean = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari: boolean = /^((?!chrome|android).)*safari/i.test(userAgent);
  return isIOS && isSafari;
};

const getUserLoggedInCount = (email: string) =>
  Number(window.localStorage.getItem(getUserLoggedInCountLocalStorageKey(email)) || 0);

const getUserFaceIdCancelledCount = (customerId: string) =>
  Number(window.localStorage.getItem(getUserFaceIdCancelledCountLocalStorageKey(customerId)) || 0);

const getFaceIdCustomerInfo = (): {
  id: string;
  email: string;
} | null => {
  try {
    const storedUserDetails = JSON.parse(
      atob(window.localStorage.getItem(FACE_ID_ENABLED_CUTOMER_DETAILS) || '') || '{}'
    );
    return { ...storedUserDetails, id: (storedUserDetails?.id || '').replace('gid://shopify/Customer/', '') };
  } catch (err) {
    console.error(err);
    return null;
  }
};

const showFaceIdLoginButton = () =>
  isSafariOnIOS() && getFaceIdCustomerInfo()?.id && window.getSiteSettings()?.faceID?.enabled;

const prePopulateFaceIDEmail = (formType: SignInModalTab) =>
  showFaceIdLoginButton() && formType === 'sign-in' ? getFaceIdCustomerInfo()?.email || '' : '';

const incrementUserLoggedInCount = (email: string | null) => {
  if (email) {
    // Counted on register and each login in a particular device.
    const previousCount = getUserLoggedInCount(email);
    window.localStorage.setItem(getUserLoggedInCountLocalStorageKey(email), String(previousCount + 1));
  }
};

const incrementUserFaceIdCancelledCount = (customerId: string) => {
  if (customerId) {
    const previousCount = getUserFaceIdCancelledCount(customerId);
    window.localStorage.setItem(getUserFaceIdCancelledCountLocalStorageKey(customerId), String(previousCount + 1));
  }
};

const showFaceIdEnableModal = (): boolean => {
  try {
    const faceIdEnabled = window.getSiteSettings()?.faceID?.enabled;
    const customerInfo = window.getCustomerInfo && window.getCustomerInfo();

    const customerId = customerInfo?.customerId?.toString() || null;
    const email = customerInfo?.email || null;
    if (!customerId || !email) return false;

    const customerIDStored = getFaceIdCustomerInfo()?.id;
    const userCancelledFaceId = getCookie(getUserCancelledFaceIdCookieKey(customerId));
    // This is to show enable-popup only when a customer re-login in a particular device.
    const isUserReLoggedIn = getUserLoggedInCount(email) > 1;

    return (
      !IS_NATIVE_APP_CLIENT &&
      isSafariOnIOS() &&
      faceIdEnabled &&
      isUserReLoggedIn &&
      !userCancelledFaceId &&
      customerIDStored !== customerId
    );
  } catch (err) {
    console.error(err);
    return false;
  }
};

const loadFaceIdScript = (): Promise<boolean> => {
  return new Promise<boolean>((resolve, reject) => {
    // Check if the SimpleWebAuthnBrowser is already available
    if (window.SimpleWebAuthnBrowser) {
      if (window.SimpleWebAuthnBrowser.browserSupportsWebAuthn()) {
        resolve(true);
      } else {
        console.error('SimpleWebAuthnBrowser is not loaded');
        resolve(false);
      }
    } else {
      // Script is not loaded yet, so we need to add it
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/@simplewebauthn/browser@10.0.0/dist/bundle/index.umd.min.js';
      script.integrity = 'sha384-SijkeUvZZs1bto8G/GPfjLDAg4FRZB5yGFAdMT5T7n3bMnkfFMEhE/h11ClJPNFR';
      script.crossOrigin = 'anonymous';
      script.async = true;

      script.onload = () => {
        if (window.SimpleWebAuthnBrowser && window.SimpleWebAuthnBrowser.browserSupportsWebAuthn()) {
          resolve(true);
        } else {
          console.error('SimpleWebAuthnBrowser is not loaded');
          resolve(false);
        }
      };

      script.onerror = () => {
        console.error('SimpleWebAuthnBrowser failed to load');
        reject(false);
      };

      document.head.appendChild(script);
    }
  });
};

const FACE_ID = {
  storeUserDetailsToLS,
  getUserCancelledFaceIdCookieKey,
  showFaceIdEnableModal,
  incrementUserLoggedInCount,
  prePopulateFaceIDEmail,
  getFaceIdCustomerInfo,
  showFaceIdLoginButton,
  getUserLoggedInCount,
  loadFaceIdScript,
  incrementUserFaceIdCancelledCount,
  getUserFaceIdCancelledCount,
};

export default FACE_ID;
